import {
	ArrowLeftIcon,
	BaseIcon,
	CommentIcon,
	CountersignatureIcon,
	EthereumIcon,
	FarcasterIcon,
	HomeIcon,
	ImageIcon,
	InstagramIcon,
	LocationIcon,
	MoreIcon,
	NewsIcon,
	PolygonIcon,
	PricetagIcon,
	RollupIcon,
	ShapeIcon,
	SolanaIcon,
	TezosIcon,
	TokenIcon,
	TwitterIcon,
	UniqueIcon,
	ZoraIcon,
} from '../icons/index.ts'
import Tag from '../badgesAndTags/tag.tsx'
import {
	TypographyHeading,
	TypographyParagraph,
	TypographyLabel,
	TypographyCaps,
} from '../typography/index.ts'
import { ReactNode, useEffect, useRef, useState } from 'react'
import Timeline from '../timeline/timeline.tsx'
import Counter from '../badgesAndTags/counter.tsx'
import Filter from '../inputs/filter.tsx'
import clsx from 'clsx'
import { formatMetadataType } from '#app/utils/misc.tsx'
import { Link } from '@remix-run/react'
import Loader from '../loaders/loader.tsx'
import CertifyIcon from '../icons/Miscellaneous/CertifyIcon.tsx'
import { AttestationType } from '#types/loreTypes.tsx'

export default function Feed({
	filter,
	atomicLoreUrl,
}: {
	filter?: string
	atomicLoreUrl: string
}) {
	type FilterOption = {
		title: string
		slug: string
		icon: ReactNode
	}

	type FilterCategory = {
		title: string
		slug: string
		options: FilterOption[]
	}

	const sortOptions = [
		{ title: 'Newest', description: 'DESC' },
		{ title: 'Oldest', description: 'ASC' },
	]

	const filterOptions = [
		{
			title: 'templates',
			slug: 'templates',
			options: [
				{ title: 'Collection', slug: 'collection', icon: <CertifyIcon /> },
				{ title: 'Comment', slug: 'comment', icon: <CommentIcon /> },
				{
					title: 'Exhibition',
					slug: 'proofOfExhibition',
					icon: <LocationIcon />,
				},
				/*{ title: 'Instagram Post', slug: 'instagram', icon: <InstagramIcon /> },*/
				{ title: 'Media citation', slug: 'mediaCitation', icon: <NewsIcon /> },
				{ title: 'Media Upload', slug: 'media', icon: <ImageIcon /> },
				{ title: 'Tweet', slug: 'tweet', icon: <TwitterIcon /> },
				{ title: 'Cast', slug: 'cast', icon: <FarcasterIcon /> },
				{
					title: 'Offchain sell',
					slug: 'offchainSale',
					icon: <PricetagIcon />,
				},
			],
		},
		{
			title: 'blockchain',
			slug: 'chains',
			options: [
				{ title: 'Base', slug: 'base', icon: <BaseIcon /> },
				{ title: 'Ethereum', slug: 'ethereum', icon: <EthereumIcon /> },
				{ title: 'Solana', slug: 'solana', icon: <SolanaIcon /> },
				{ title: 'Tezos', slug: 'tezos', icon: <TezosIcon /> },
				{ title: 'Zora', slug: 'zora', icon: <ZoraIcon /> },
				{ title: 'Shape', slug: 'shape', icon: <ShapeIcon /> },
				{ title: 'Polygon', slug: 'polygon', icon: <PolygonIcon /> },
			],
		},
		{
			title: 'attestations',
			slug: 'types',
			options: [
				{ title: 'Unique', slug: 'unique', icon: <UniqueIcon /> },
				{
					title: 'Countersignature',
					slug: 'countersign',
					icon: <CountersignatureIcon />,
				},
				{ title: 'Rollup', slug: 'rollup', icon: <RollupIcon /> },
			],
		},
	]

	const findFilterOption = (
		filterOptions: FilterCategory[],
		filter: string,
	) => {
		let foundCategory
		let foundOption
		for (const category of filterOptions) {
			const found = category.options.find(option => option.slug === filter)
			if (found) {
				foundCategory = category
				foundOption = found
			}
		}
		if (foundCategory && foundOption) {
			return {
				name: foundCategory.slug,
				value: foundOption.slug,
				icon: foundOption.icon,
			}
		}
		return null
	}

	const compareDates = (a, b) => {
		const aComparisonDate = a.comparisonDate || a.createdAt
		const bComparisonDate = b.comparisonDate || b.createdAt
		const dateA = new Date(aComparisonDate)
		const dateB = new Date(bComparisonDate)

		switch (filters.sortCriteria) {
			case 'DESC':
				return dateB - dateA
			case 'ASC':
				return dateA - dateB
			default:
				return dateB - dateA
		}
	}

	const [metadataList, setMetadataList] = useState<AttestationType[] | null>(
		null,
	)
	const [totalMetadata, setTotalMetadata] = useState(0)
	const [filters, setFilters] = useState<{
		currentPage: number
		sortCriteria: string
		filterCriteria: { name: string; value: string; icon: any } | null
	}>({
		currentPage: 0,
		sortCriteria: 'DESC',
		filterCriteria:
			filter && findFilterOption(filterOptions, filter)
				? findFilterOption(filterOptions, filter)
				: null,
	})
	const [loading, setLoading] = useState(false)
	const prevCurrentPage = useRef(filters.currentPage)
	const [isMenuExpanded, setIsMenuExpanded] = useState(false)

	useEffect(() => {
		loadMetadata()
	}, [filters])

	useEffect(() => {
		setLoading(false)
	}, [metadataList])

	const loadMetadata = async () => {
		if (loading) return
		setLoading(true)
		prevCurrentPage.current = filters.currentPage
		setTimeout(async () => {
			if (prevCurrentPage.current == filters.currentPage) {
				try {
					const options = {
						method: 'GET',
						headers: { 'Content-Type': 'application/json' },
					}
					const response = await fetch(
						`/attestationsAPI?page=${filters.currentPage}&sort=${filters.sortCriteria}${filters.filterCriteria ? `&${filters.filterCriteria.name}=${filters.filterCriteria.value}` : ''}`,
						options,
					)
					const metadataIndex = (await response.json()) as {
						mintedMetadata: AttestationType[]
						totalMetadata: number
					}
					setTotalMetadata(metadataIndex.totalMetadata)
					filters.currentPage == 0
						? setMetadataList(metadataIndex.mintedMetadata)
						: setMetadataList([
								...metadataList,
								...metadataIndex.mintedMetadata,
							])
				} catch (e) {
					console.error(e)
				}
			}
		}, 300)
	}

	return (
		<div className="mx-auto max-w-[1440px]">
			<div className="flex flex-row lg:gap-x-0 xl:gap-x-24">
				<div
					className={`
						absolute z-10 flex flex-col border-r border-uiTertiaryLight bg-white
						transition-all duration-300 dark:border-uiTertiaryDark dark:bg-black sm:pointer-events-auto
						sm:relative sm:min-w-80 sm:max-w-80 sm:opacity-100
						${isMenuExpanded ? 'min-w-80 max-w-80 opacity-100' : 'pointer-events-none min-w-0 max-w-0 opacity-0'}
					`}
				>
					<Link
						to={'/'}
						className="flex flex-row items-center gap-x-2 border-b border-uiTertiaryLight pb-6 pl-12 pt-12 text-textSecondaryLight dark:border-uiTertiaryDark dark:text-textSecondaryDark"
					>
						<HomeIcon />
						<TypographyLabel
							size="large"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Atomic Lore
						</TypographyLabel>
					</Link>
					<div className="flex flex-col pl-12">
						<div className="flex flex-col py-4">
							<FilterOption
								name="All"
								icon={<TokenIcon />}
								onClick={() => {
									if (window?.innerWidth < 768) setIsMenuExpanded(false)
									setFilters({
										...filters,
										currentPage: 0,
										filterCriteria: null,
									})
								}}
								selected={filters.filterCriteria == null}
							/>
						</div>
						{filterOptions.map(optionsSection => {
							return (
								<div
									className="mb-4 flex flex-col"
									key={`options-section-${optionsSection.title}`}
								>
									<TypographyCaps
										size="small"
										additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark mb-2"
									>
										{optionsSection.title}
									</TypographyCaps>
									{optionsSection.options.map(option => {
										return (
											<div key={`option-${option.title}`}>
												<FilterOption
													name={option.title}
													icon={option.icon}
													onClick={() => {
														if (window?.innerWidth < 768)
															setIsMenuExpanded(false)
														setFilters({
															...filters,
															currentPage: 0,
															filterCriteria: {
																name: optionsSection.slug,
																value: option.slug,
																icon: option.icon,
															},
														})
													}}
													selected={
														filters.filterCriteria?.value == option.slug
													}
												/>
											</div>
										)
									})}
								</div>
							)
						})}
					</div>
				</div>
				<div
					className={`flex min-w-0 flex-1 flex-col px-4 py-16 md:px-12 md:pr-32 ${isMenuExpanded ? 'opacity-50 sm:opacity-100' : ''}`}
				>
					<div
						className="flex flex-row items-center gap-x-4 text-textPrimaryLight dark:text-textPrimaryDark sm:hidden"
						onClick={() => setIsMenuExpanded(!isMenuExpanded)}
					>
						<TypographyLabel
							size="large"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Filters:
						</TypographyLabel>
						<div className="flex flex-row items-center text-textSecondaryLight dark:text-textSecondaryDark">
							<div className="ml-2 mr-2 flex h-full items-center">
								{filters.filterCriteria ? (
									filters.filterCriteria?.icon
								) : (
									<TokenIcon />
								)}
							</div>
							<TypographyLabel
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								{filters.filterCriteria
									? formatMetadataType(filters.filterCriteria?.value)
									: 'All templates'}
							</TypographyLabel>
						</div>
					</div>
					<TypographyHeading
						size="large"
						isBold={true}
						additionalClasses="mt-4 justify-center text-textPrimaryLight dark:text-textPrimaryDark"
					>
						Lore Feed
					</TypographyHeading>
					<TypographyParagraph
						size="medium"
						additionalClasses="mt-6 pb-8 mb-8 border-b border-textInactiveLight dark:border-uiTertiaryDark justify-center text-textSecondaryLight dark:text-textSecondaryDark"
					>
						This space shows the latest attestations from the NFTs, Collections
						and Artist Profiles in Atomic Lore
					</TypographyParagraph>

					{(loading && filters.currentPage == 0) || metadataList === null ? (
						<div className="flex flex-row items-center justify-center">
							<Loader />
						</div>
					) : metadataList && metadataList.length ? (
						<>
							<div className="mb-4 flex flex-row items-center justify-between gap-x-2">
								<div className="flex flex-row items-center gap-x-2">
									<TypographyHeading
										size="medium"
										isBold={true}
										additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
									>
										Attestations
									</TypographyHeading>
									<Counter size="small">{totalMetadata}</Counter>
								</div>
								<Filter
									type="Sort"
									options={sortOptions}
									value={sortOptions.find(
										option => option.description == filters.sortCriteria,
									)}
									onChange={newVal =>
										setFilters({
											...filters,
											currentPage: 0,
											sortCriteria: newVal.description,
										})
									}
								></Filter>
							</div>
							<Timeline
								// @ts-ignore
								metadataEntries={metadataList.sort(compareDates)}
								metadataAdditionalClasses="w-full"
								avatarSize={window?.innerWidth > 768 ? 'large' : 'small'}
								showVerticalLine={false}
								showDividers={true}
								tweetsUrl={`${atomicLoreUrl}/getTweet`}
								noContentText="Looks like there’s no Lore matching your filter selection. Try adjusting the filters or clearing them to explore more stories."
							/>
						</>
					) : (
						<div className="mt-12 flex flex-col items-center justify-center">
							<TypographyParagraph
								size="small"
								isBold={true}
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								No Lore Found
							</TypographyParagraph>
							<TypographyParagraph
								size="small"
								additionalClasses="mt-4 text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Looks like there’s no Lore matching your filter selection.
							</TypographyParagraph>
							<TypographyParagraph
								size="small"
								additionalClasses="mt-2 text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Try adjusting the filters or clearing them to explore more
								stories.
							</TypographyParagraph>
						</div>
					)}
					{!loading && metadataList && (
						<div className="mt-4 flex w-full items-center justify-center">
							<Tag
								showIcon={false}
								onClick={() =>
									setFilters({
										...filters,
										currentPage: filters.currentPage + 1,
									})
								}
							>
								<MoreIcon />
							</Tag>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

const FilterOption = ({
	name,
	icon,
	onClick,
	selected,
}: {
	name: string
	icon?: any
	onClick: () => void
	selected?: boolean
}) => {
	return (
		<div
			key={name}
			className={clsx(
				'flex cursor-pointer items-center',
				'w-full border-l-2 border-textSecondaryLight py-2.5 text-labelMediumBold',
				selected
					? 'border-solid border-textPrimaryLight bg-uiTertiaryLight text-textPrimaryLight dark:border-textPrimaryDark dark:bg-uiTertiaryDark dark:text-textPrimaryDark'
					: 'border-dotted text-textSecondaryLight dark:border-textSecondaryDark dark:text-textSecondaryDark',
				'disabled:border-transparent disabled:text-textInactiveLight',
				'hover:border-solid hover:border-textPrimaryLight hover:bg-uiTertiaryLight hover:text-textSecondaryLight',
				'dark:hover:border-textSecondaryDark dark:hover:bg-uiTertiaryDark dark:hover:text-textSecondaryDark',
				'dark:disabled:border-l-2 dark:disabled:border-solid dark:disabled:border-textInactiveDark dark:disabled:text-textInactiveDark',
				'dark:disabled:hover:border-textInactiveDark',
				'outline-none ring-0',
			)}
			onClick={onClick}
		>
			{icon && <div className="ml-2 mr-2 flex h-full items-center">{icon}</div>}
			<TypographyLabel size="medium">{name}</TypographyLabel>
		</div>
	)
}
